import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Clock3, RefreshCw, Save, Swords } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";


const fighterName = (fighter) => fighter ? `${fighter.first_name} ${fighter.last_name}` : "Offen";

export default function RingSchedulePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState("");
  const canManage = ["super_admin", "event_admin"].includes(user?.role);

  const load = useCallback(async () => {
    const [eventResponse, scheduleResponse] = await Promise.all([api.get(`/events/${id}`), api.get(`/events/${id}/ring-schedule`)]);
    setEvent(eventResponse.data);
    setMatches(scheduleResponse.data);
  }, [id]);

  useEffect(() => {
    load().catch((requestError) => setError(apiError(requestError)));
  }, [load]);

  const autoPlan = async () => {
    setError("");
    try {
      const response = await api.post(`/events/${id}/schedule/auto`);
      setNotice(response.data.message);
      await load();
    } catch (requestError) {
      setError(apiError(requestError));
    }
  };

  const updateSchedule = async (match, key, value) => {
    const next = matches.map((item) => item.id === match.id ? { ...item, [key]: key === "ring_number" ? Number(value) : value } : item);
    setMatches(next);
  };

  const saveMatch = async (match) => {
    setSaving(match.id);
    try {
      const response = await api.put(`/matches/${match.id}/schedule`, { ring_number: match.ring_number, scheduled_time: match.scheduled_time });
      setMatches(matches.map((item) => item.id === match.id ? { ...item, ...response.data } : item));
      setNotice("Ring und Startzeit wurden aktualisiert.");
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setSaving("");
    }
  };

  if (error && !event) return <div className="content-frame"><div className="form-error" data-testid="ring-schedule-error">{error}</div></div>;
  if (!event) return <div className="content-frame loading" data-testid="ring-schedule-loading">Ringplanung wird geladen …</div>;
  const rings = Array.from({ length: event.rings }, (_, index) => index + 1);
  return <div className="content-frame" data-testid="ring-schedule-page"><Link to={`/events/${id}`} className="back-link" data-testid="back-to-event-from-rings-link">← ZUM EVENT</Link><header className="page-heading"><div><p className="eyebrow">FREIGEGEBENE KÄMPFE</p><h1>RINGPLAN</h1><p className="muted">{event.name} · nach Ring und Startzeit organisiert</p></div>{canManage && <button className="button primary" onClick={autoPlan} data-testid="auto-schedule-button"><RefreshCw size={18} /> OFFENE KÄMPFE VERTEILEN</button>}</header>{notice && <div className="success-notice" data-testid="ring-schedule-notice">{notice}</div>}{error && <div className="form-error" data-testid="ring-schedule-action-error">{error}</div>}<section className="rings-grid">{rings.map((ring) => <article className="ring-column" key={ring} data-testid={`ring-column-${ring}`}><header><span>RING</span><h2>{ring}</h2><small>{matches.filter((match) => match.ring_number === ring).length} KÄMPFE</small></header>{matches.filter((match) => match.ring_number === ring).map((match) => <div className="ring-match" key={match.id} data-testid={`ring-match-${match.id}`}><div className="ring-match-time"><Clock3 size={15} />{match.scheduled_time ? new Date(match.scheduled_time).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "OFFEN"}</div><div className="ring-fighters"><span className="blue-corner-label">BLAU</span><b>{fighterName(match.fighter1)}</b><span className="red-corner-label">ROT</span><b>{fighterName(match.fighter2)}</b></div><small>Runde {match.round} · {match.status === "completed" ? "BEENDET" : "GEPLANT"}</small>{canManage && <div className="ring-edit"><select value={match.ring_number} onChange={(event) => updateSchedule(match, "ring_number", event.target.value)} data-testid={`ring-select-${match.id}`}>{rings.map((number) => <option value={number} key={number}>Ring {number}</option>)}</select><input type="datetime-local" value={match.scheduled_time?.slice(0, 16) || ""} onChange={(event) => updateSchedule(match, "scheduled_time", event.target.value)} data-testid={`ring-time-${match.id}`} /><button className="icon-button" onClick={() => saveMatch(match)} disabled={saving === match.id} data-testid={`save-ring-match-${match.id}`}><Save size={16} /></button></div>}</div>)}{!matches.some((match) => match.ring_number === ring) && <div className="ring-empty" data-testid={`ring-empty-${ring}`}><CalendarClock size={22} /><span>Noch keine angesetzten Kämpfe</span></div>}</article>)}</section>{!matches.length && <div className="empty-state" data-testid="ring-schedule-empty-state"><Swords size={32} /><h2>NOCH KEINE KAMPFPAARUNGEN</h2><p>Verteile die kampfbereiten Begegnungen automatisch auf die vorhandenen Ringe.</p></div>}</div>;
}
